/**
 * Centralized API client with automatic 401 handling
 *
 * This is the SINGLE SOURCE OF TRUTH for all API communication.
 * All auth token injection and 401 handling is centralized here (DRY principle).
 *
 * Features:
 * - Automatic auth token injection from localStorage
 * - Global 401 interceptor (clears token, redirects to login with a notice)
 * - Consistent error handling
 * - Request logging for debugging
 */

import { friendlyApiErrorMessage } from "./apiErrorDetail";
import { getApiBaseURLFromEnv } from "./env";
import {
	isAuthEndpoint,
	redirectToLoginForExpiredSession,
	redirectToSupportEnded,
	SESSION_EXPIRED_MESSAGE,
	SessionExpiredError,
} from "./sessionExpiry";
import {
	clearSupportToken,
	getSessionToken,
	isSupportSession,
} from "./sessionToken";

// Resolve API base at call time to pick up runtime env (window.__ENV__) reliably
const resolveApiBaseUrl = (): string => getApiBaseURLFromEnv() ?? "";

/**
 * Enhanced fetch wrapper that automatically handles 401 responses
 *
 * @param endpoint - API endpoint (relative or absolute URL)
 * @param options - Standard fetch options
 * @returns Response object
 *
 * On 401 Unauthorized (except from auth endpoints, whose 401 means "bad
 * credentials" and is returned to the caller):
 * 1. Clears access_token from localStorage
 * 2. Redirects user to /login?next=<current path> with a session-expired notice
 * 3. Throws error to prevent further processing
 */
export async function apiFetch(
	endpoint: string,
	options: RequestInit = {},
): Promise<Response> {
	// The signed-in user's token, or a support session's (DOP-562)
	const token = getSessionToken();

	// Build headers with auth if token exists
	const headers: Record<string, string> = {
		...(options.headers as Record<string, string>),
	};

	// Add Authorization header for all protected endpoints
	// (Skip for auth endpoints like /auth/login, /auth/register)
	if (
		token &&
		!endpoint.includes("/auth/jwt/") &&
		!endpoint.includes("/auth/register")
	) {
		headers["Authorization"] = `Bearer ${token}`;
	}

	// Build full URL
	const base = resolveApiBaseUrl();
	const url = endpoint.startsWith("http") ? endpoint : `${base}${endpoint}`;

	console.log(`[API] ${options.method || "GET"} ${url}`);

	// Make the request
	const response = await fetch(url, {
		...options,
		headers,
	});

	// Handle 401 Unauthorized - token is invalid/expired/revoked
	if (response.status === 401) {
		// A 401 from the login/signup/password endpoints means "bad credentials",
		// not "your session expired" — hand it back so the form shows its own
		// error instead of bouncing the user through an expiry notice.
		if (isAuthEndpoint(endpoint)) {
			return response;
		}

		// A slow request can answer 401 long after another tab logged back in.
		// Clearing then would delete a token that is perfectly good, logging the
		// user out of every tab. If the stored token is no longer the one this
		// request carried, the 401 is stale: hand it back untouched.
		const currentToken = getSessionToken();
		if (currentToken && currentToken !== token) {
			console.warn(
				"[API] Ignoring stale 401 - the session was renewed while this request was in flight",
			);
			return response;
		}

		// A support session that stops authenticating is over (DOP-562). Only
		// ITS token goes: `access_token` is the operator's own login to this
		// deployment and was never part of the session.
		if (isSupportSession()) {
			clearSupportToken();
			redirectToSupportEnded();
			throw new SessionExpiredError("This support session has ended.");
		}

		console.error(
			"[API] 401 Unauthorized - clearing auth and redirecting to login",
		);

		// Clear token
		localStorage.removeItem("access_token");
		console.log("[API] Token removed from localStorage");

		// Redirect to /login with a session-expired notice and a `?next=` so
		// re-login lands the user back on the page they were using (DOP-267).
		redirectToLoginForExpiredSession();

		// Throw error to prevent further processing
		throw new SessionExpiredError(SESSION_EXPIRED_MESSAGE);
	}

	return response;
}

/**
 * Helper for JSON GET requests
 * Automatically includes auth token and handles 401 responses
 */
export async function apiGet<T>(endpoint: string): Promise<T> {
	const response = await apiFetch(endpoint, {
		method: "GET",
	});

	if (!response.ok) {
		// GET used to throw the status line verbatim — "API Error: 500 Internal
		// Server Error" — and components rendered it. Reads go through the same
		// friendly mapping the writes have always used.
		const error = await response.json().catch(() => ({}));
		throw new Error(friendlyApiErrorMessage(response.status, error.detail));
	}

	try {
		return await response.json();
	} catch (jsonError) {
		console.error("API response is not valid JSON:", jsonError);
		throw new Error(
			"We couldn't read the server's response. Please try again.",
		);
	}
}

/**
 * Helper for JSON POST requests
 * Automatically includes auth token and handles 401 responses
 */
export async function apiPost<T>(endpoint: string, data?: any): Promise<T> {
	const response = await apiFetch(endpoint, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: data ? JSON.stringify(data) : undefined,
	});

	if (!response.ok) {
		const error = await response.json().catch(() => ({}));
		// `detail` may be an object carrying a code (e.g. `no_client_scope`);
		// interpolating it raw printed "[object Object]" to the user. When it
		// carries no sentence at all, the status becomes one.
		throw new Error(friendlyApiErrorMessage(response.status, error.detail));
	}

	try {
		return await response.json();
	} catch (jsonError) {
		console.error("API response is not valid JSON:", jsonError);
		throw new Error(
			"We couldn't read the server's response. Please try again.",
		);
	}
}

/**
 * Helper for JSON PUT requests
 * Automatically includes auth token and handles 401 responses
 */
export async function apiPut<T>(endpoint: string, data?: any): Promise<T> {
	const response = await apiFetch(endpoint, {
		method: "PUT",
		headers: {
			"Content-Type": "application/json",
		},
		body: data ? JSON.stringify(data) : undefined,
	});

	if (!response.ok) {
		const error = await response.json().catch(() => ({}));
		// `detail` may be an object carrying a code (e.g. `no_client_scope`);
		// interpolating it raw printed "[object Object]" to the user. When it
		// carries no sentence at all, the status becomes one.
		throw new Error(friendlyApiErrorMessage(response.status, error.detail));
	}

	try {
		return await response.json();
	} catch (jsonError) {
		console.error("API response is not valid JSON:", jsonError);
		throw new Error(
			"We couldn't read the server's response. Please try again.",
		);
	}
}

/**
 * Helper for JSON DELETE requests
 * Automatically includes auth token and handles 401 responses
 */
export async function apiDelete<T>(endpoint: string): Promise<T> {
	const response = await apiFetch(endpoint, {
		method: "DELETE",
		headers: {
			"Content-Type": "application/json",
		},
	});

	if (!response.ok) {
		const error = await response.json().catch(() => ({}));
		// `detail` may be an object carrying a code (e.g. `no_client_scope`);
		// interpolating it raw printed "[object Object]" to the user. When it
		// carries no sentence at all, the status becomes one.
		throw new Error(friendlyApiErrorMessage(response.status, error.detail));
	}

	// Handle 204 No Content
	if (response.status === 204) {
		return {} as T;
	}

	try {
		return await response.json();
	} catch (jsonError) {
		console.error("API response is not valid JSON:", jsonError);
		throw new Error(
			"We couldn't read the server's response. Please try again.",
		);
	}
}

/**
 * Unified API client export
 * Use this for direct access to all API methods
 */
export const apiClient = {
	fetch: apiFetch,
	get: apiGet,
	post: apiPost,
	put: apiPut,
	delete: apiDelete,
};
